import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DocumentHeader, GlobalLayers, Layers } from "./components";
import styles from "./DocumentViewer.module.css";
import { useApi, useCurrentHeight } from "../../../../common/hooks";
import {
	getDocumentPhysicalDetails,
	resetDocument,
	setSelectedDocument,
} from "../../slice/notes-selected-document/notes-selected-document-slice";
import { PdfViewer } from "../../../../common/components";
import { ApiService, MyService } from "../../../../api";
import { computeAbsoluteTop } from "../../../my-review/utils/utils";
import { getMetadata, getPage } from "../../slice/notes-pdf/notes-pdf-thunk";
import { setPage, setScrollTo, setSelection } from "../../slice/notes-pdf/notes-pdf-slice";
import { getPageSlotHeight, isNonEmptyArray } from "../../../../common/utils";
import { NavUtils } from "../../../../navigation";

const DocumentViewer = ({ editor, onCloseDocViewer }) => {
	const { call: getDocumentDetails } = useApi(MyService.getDocumentDetails);
	const projectId = useSelector(({ context }) => context.project.id);
	const cautionBannerDisabled = useSelector(({ context }) => context.cautionBannerHidden);
	const selectedDocument = useSelector(({ notesSelectedDocument }) => notesSelectedDocument.selectedDocument);
	const numberOfPages = useSelector(({ notesSelectedDocument }) => notesSelectedDocument.numberOfPages);
	const pageDimensions = useSelector(({ notesSelectedDocument }) => notesSelectedDocument.pageDimensions);
	const srPage = useSelector(({ notesPdf }) => notesPdf.page);
	const zoom = useSelector(({ notesPdf }) => notesPdf.zoom);
	const content = useSelector(({ notesPdf }) => notesPdf.content);
	const scrollTo = useSelector(({ notesPdf }) => notesPdf.scrollTo);
	const isGenerating = useSelector(({ note }) => note.isGenerating);
	const newlyGeneratedNote = useSelector(({ note }) => note.newlyGeneratedNote);
	const [end, setEnd] = useState(null);
	const start = useRef(null);
	const cancelTokenSourceRef = useRef(null);
	const containerRef = useRef();
	const scrollRef = useRef();
	const params = useParams();
	const location = useLocation();
	const dispatch = useDispatch();
	const restrictedContent = useMemo(
		() =>
			(content.filter((c) => c.page - 1 >= srPage - 5 && c.page - 1 < srPage + 5) || []).map((cf) => ({
				...cf,
				top: computeAbsoluteTop(pageDimensions, cf.page, 0, zoom),
			})),
		[content, srPage, pageDimensions, zoom]
	);
	const getAllDocumentDetails = useCallback(() => {
		let docId = Number.parseInt(params?.docId, 10);
		if (docId && docId !== selectedDocument?.id) {
			getDocumentDetails({ docId })
				.then((data) => {
					dispatch(
						setSelectedDocument({
							id: docId,
							name: data.documentName,
							label: data.documentFileName,
							projectId,
						})
					);
				})
				.catch(console.error);
		} else if (selectedDocument?.id && selectedDocument.projectId === projectId) {
			docId = selectedDocument?.id;
			window.history.pushState({}, "", NavUtils.goToNotes({ projectId, documentId: docId, page: 1 }));
		}
		if (docId) {
			dispatch(getDocumentPhysicalDetails({ docId, token: cancelTokenSourceRef.current.token, projectId }));
			dispatch(setPage(0));
		}
	}, [params, dispatch, getDocumentDetails, projectId, selectedDocument]);
	const handleScrollToLocation = useCallback(() => {
		const { y1, y2, page } = scrollTo;
		let scrollToY = 0;
		let scrollFromTop = 0;
		let paragraphVerticalCenteringOffset = 0;
		if (y1) {
			scrollToY = y1;
			if (y2) {
				paragraphVerticalCenteringOffset = ((pageDimensions[page].height / 4) * zoom) / 100;
				scrollToY = (y1 + y2) / 2;
			}
		}
		for (let i = 0; i < page; i++) {
			scrollFromTop += (getPageSlotHeight(pageDimensions[i]) * zoom) / 100;
		}
		scrollFromTop += (pageDimensions[page].height * scrollToY * zoom) / 100 - paragraphVerticalCenteringOffset;
		scrollRef.current.scrollTo({
			top: scrollFromTop,
			behavior: "auto",
		});
		dispatch(setScrollTo({}));
	}, [scrollRef, scrollTo, pageDimensions, zoom, dispatch]);
	const handleInitPageSelect = useCallback(() => {
		const urlPage = Number.parseInt(new URLSearchParams(location.search).get("page"), 10);
		if (urlPage) {
			dispatch(setPage(urlPage));
			dispatch(setScrollTo({ page: urlPage, x1: 0.01 }));
		}
	}, [location, dispatch]);
	const handleGetPage = useCallback(
		(page) => {
			if (!content.some((c) => c.page === page) && cancelTokenSourceRef?.current) {
				dispatch(getPage({ docId: selectedDocument?.id, page, token: cancelTokenSourceRef.current.token }));
			}
		},
		[content, dispatch, selectedDocument]
	);
	const handleGetPageMetadata = (page) => {
		if (
			!content.some((c) => c.page === page && c.overlay) &&
			selectedDocument?.id &&
			cancelTokenSourceRef?.current
		) {
			dispatch(getMetadata({ docId: selectedDocument?.id, page, token: cancelTokenSourceRef.current.token }));
		}
	};
	useEffect(() => {
		if (!cancelTokenSourceRef.current) {
			cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		}
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);
	useEffect(() => {
		getAllDocumentDetails();
	}, [getAllDocumentDetails]);
	useEffect(() => {
		handleInitPageSelect();
	}, [handleInitPageSelect]);
	useEffect(() => {
		dispatch(setPage(Number.parseInt(new URLSearchParams(location.search).get("page"), 10) - 1));
	}, [location, dispatch]);
	useEffect(() => {
		if (
			scrollRef.current &&
			scrollTo &&
			(scrollTo?.page || scrollTo?.page === 0) &&
			isNonEmptyArray(pageDimensions) &&
			zoom
		) {
			handleScrollToLocation();
		}
	}, [scrollRef, scrollTo, pageDimensions, handleScrollToLocation, zoom]);

	useEffect(() => {
		const handleMouseDown = (e) => {
			const parentRect = document.getElementById("pdfViewerList_id").getBoundingClientRect();
			const top = e.clientY - parentRect.top;
			let page = 0;
			let pageHeight = 0;
			while (pageHeight < top) {
				pageHeight += (getPageSlotHeight(pageDimensions[page]) * zoom) / 100;
				page++;
			}
			start.current = {
				left: e.clientX - parentRect.left,
				top,
				page,
				pageTopCoordinate: pageHeight - (getPageSlotHeight(pageDimensions[page - 1]) * zoom) / 100,
			};
		};
		const handleMouseMove = (e) => {
			if (start.current === null) {
				return;
			}
			const parentRect = document.getElementById("pdfViewerList_id").getBoundingClientRect();
			const newLeft = e.clientX - parentRect.left;
			const newTop = e.clientY - parentRect.top;
			let page = 0;
			let pageHeight = 0;
			while (pageHeight < newTop) {
				pageHeight += (getPageSlotHeight(pageDimensions[page]) * zoom) / 100;
				page++;
			}
			setEnd({
				left: newLeft,
				top: newTop,
				page,
			});
		};
		const handleMouseUp = () => {
			start.current = null;
			setEnd(null);
		};
		const pdfViewerList = document.getElementById("pdfViewerList_id");
		pdfViewerList.addEventListener("mousedown", handleMouseDown);
		pdfViewerList.addEventListener("mousemove", handleMouseMove);
		document.addEventListener("mouseup", handleMouseUp);
		return () => {
			pdfViewerList.removeEventListener("mousedown", handleMouseDown);
			pdfViewerList.removeEventListener("mousemove", handleMouseMove);
			document.removeEventListener("mouseup", handleMouseUp);
		};
	}, [start, end, pageDimensions, zoom]);
	const getContentsInSelection = useCallback(() => {
		let yOffset = start?.current?.pageTopCoordinate || 0;
		const beginPage = Math.min(start.current?.page, end?.page);
		const endPage = Math.max(start.current?.page, end?.page);
		const left = Math.min(start.current?.left, end?.left);
		const right = Math.max(start.current?.left, end?.left);
		const top = Math.min(start.current?.top, end?.top);
		const bottom = Math.max(start.current?.top, end?.top);
		const selection = [];
		for (let i = beginPage; i <= endPage; i++) {
			const currentPageDimentions = pageDimensions[i - 1];
			const informations = content.find((c) => c.page === i)?.overlay?.informations;
			if (isNonEmptyArray(informations)) {
				for (const info of informations) {
					if (
						info.rectangle.x1 * currentPageDimentions.width * (zoom / 100) < right &&
						info.rectangle.x2 * currentPageDimentions.width * (zoom / 100) > left &&
						yOffset + info.rectangle.y1 * currentPageDimentions.height * (zoom / 100) < bottom &&
						yOffset + info.rectangle.y2 * currentPageDimentions.height * (zoom / 100) > top
					) {
						selection.push(info);
					}
				}
			}
			yOffset += (getPageSlotHeight(currentPageDimentions) * zoom) / 100;
		}
		dispatch(setSelection({ ids: selection, highlighted: false }));
	}, [end, content, pageDimensions, zoom, dispatch]);
	useEffect(() => {
		if (end && start.current?.page) {
			getContentsInSelection();
		}
	}, [end, getContentsInSelection]);
	const handleCloseDocViewer = () => {
		dispatch(resetDocument());
		onCloseDocViewer();
	};
	const handleChangePage = (page) => {
		const url = new URL(window.location);
		url.searchParams.set("page", page + 1);
		window.history.replaceState({}, "", url);
		dispatch(setPage(page));
	};
	const handleRenderLayers = (props) => <Layers editor={editor} {...props} />;
	const handleRenderGlobalLayers = useCallback(() => <GlobalLayers end={end} start={start.current} />, [end, start]);
	return (
		<div ref={containerRef} className={styles.container}>
			<DocumentHeader
				editor={editor}
				pdfContainerWidth={containerRef?.current?.offsetWidth}
				onClose={handleCloseDocViewer}
			/>
			<div className={styles.pdfContainer}>
				<PdfViewer
					content={restrictedContent}
					height={useCurrentHeight({
						difference:
							(isGenerating || !!newlyGeneratedNote ? -188 : -121) - (cautionBannerDisabled ? 0 : 57),
					})}
					numberOfPages={numberOfPages}
					pageDimensions={pageDimensions}
					scrollRef={scrollRef}
					width="100%"
					zoom={zoom}
					onGetMetadata={handleGetPageMetadata}
					onGetPage={handleGetPage}
					onRenderGlobalOverlay={handleRenderGlobalLayers}
					onRenderLayers={handleRenderLayers}
					onSetPage={handleChangePage}
				/>
			</div>
		</div>
	);
};
export default DocumentViewer;
